import { log } from '../loggers/Logger';
import { AutoSavingService } from './AutoSavingService';

/** Synchronously pushes an editor's debounced edit into staging. */
export type PendingFlusher = () => void;

/** Removes a previously registered flusher. Safe to call more than once. */
export type UnregisterFlusher = () => void;

/**
 * Drains every editor's pending edits to local storage in one synchronous
 * pass, so a browser close doesn't lose debounced edits.
 */
export class PendingFlushCoordinator {
  private readonly _autoSaving: AutoSavingService;
  private readonly _flushers = new Set<PendingFlusher>();

  constructor(autoSaving: AutoSavingService) {
    this._autoSaving = autoSaving;
  }

  /**
   * Register a flusher to run on a close-time drain. Returns an idempotent
   * unsubscribe for the `useEffect` cleanup pattern.
   */
  registerFlusher(flush: PendingFlusher): UnregisterFlusher {
    this._flushers.add(flush);
    let isRemoved = false;
    return () => {
      if (isRemoved) return;
      isRemoved = true;
      this._flushers.delete(flush);
    };
  }

  /**
   * Run every registered flusher. Failures are logged and isolated so one
   * broken flusher can't block the others. Callers use this when they need
   * flushers drained but don't need the autosave service persisted (e.g.
   * before a save that will commit staged edits to the backend anyway).
   */
  drainFlushers(): void {
    for (const flush of this._flushers) {
      try {
        flush();
      } catch (err) {
        log.error({ err }, 'A pending-flush flusher threw; continuing with the remaining flushers');
      }
    }
  }

  /**
   * Drain all pending edits to local storage now. Safe to call from a browser
   * close/tab-hide handler.
   */
  flushImmediately(): void {
    // Drain Layer 1: stage each editor's latest un-staged edit.
    this.drainFlushers();

    // Drain Layer 2: write the staged edits to local storage synchronously.
    this._autoSaving.flushImmediately();
  }
}
